import { useState, MouseEvent } from "react";
import { Box, ButtonBase, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { getCarrierLabel } from "@/components/common/PolicyField";
import { PolicyStatus, type Policy } from "@/types";
import { ColorType } from "@/types/shared/uiComponents";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { usePolicyContext } from "./PolicyContext";

/** "Carrier 2025–2026", the policy display convention (carrier + year, never the aegle id). */
export const policyTagLabel = (policy: Policy): string => {
  const years = formatYearRange(policy.effective_start_date, policy.effective_end_date);
  return years ? `${getCarrierLabel(policy)} ${years}` : getCarrierLabel(policy);
};

/**
 * The policy term as the header's one tag (Feature #7659, audit #2). The
 * selected term is the context every figure on the page belongs to, so it sits
 * under the name beside the client, reads "Carrier years" (no label: the chip
 * is self-describing) and opens the switcher itself
 * when the claimant is on more than one term (caret, tooltip). The policy status
 * chip shows only when the term is not active: "Active" on every page is noise,
 * "Cancelled" is the thing to notice.
 */
export const PolicyTagSwitcher = () => {
  const { policies, selectedPolicyId, setSelectedPolicyId } = usePolicyContext();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const selected = policies.find((p) => p.id === selectedPolicyId) ?? policies[0];

  if (!selected) {
    return (
      <Box
        component="span"
        data-testid="policy-tag-missing"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
      >
        <StatusChip label="No policy" color={ColorType.Default} />
      </Box>
    );
  }

  const switchable = policies.length > 1;
  const open = (e: MouseEvent<HTMLElement>) => switchable && setAnchor(e.currentTarget);
  const label = policyTagLabel(selected);
  const inactive = selected.status && selected.status !== PolicyStatus.ACTIVE;
  const tooltip = switchable
    ? `Figures on this page are for this policy term. Click to switch (${policies.length} terms).`
    : "Figures on this page are for this policy term, the only one this claimant is linked to.";

  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }} data-testid="policy-context">
      <Tooltip title={tooltip} placement="bottom-start">
        {/* span: MUI cannot attach a tooltip to a disabled button (single-term claimants). */}
        <span style={{ display: "inline-flex" }}>
          <ButtonBase
            onClick={open}
            disabled={!switchable}
            aria-haspopup={switchable ? "listbox" : undefined}
            aria-expanded={switchable ? !!anchor : undefined}
            aria-label={switchable ? `Policy ${label}. Switch policy` : `Policy ${label}`}
            data-testid="policy-tag"
            sx={{
              borderRadius: 4,
              "&:hover .MuiChip-root": { bgcolor: "action.hover" },
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            <StatusChip label={label} color={ColorType.Default} />
            {switchable ? <ArrowDropDownIcon sx={{ fontSize: 18, color: "text.secondary", ml: -0.5 }} /> : null}
          </ButtonBase>
        </span>
      </Tooltip>
      {inactive ? (
        <StatusChip label={formatToTitleCase(String(selected.status))} color={getStatusColor(selected.status)} />
      ) : null}
      {switchable ? (
        <Menu
          open={!!anchor}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          MenuListProps={{ role: "listbox", dense: true }}
        >
          {policies.map((p) => (
            <MenuItem
              key={p.id}
              role="option"
              selected={p.id === selected.id}
              aria-selected={p.id === selected.id}
              onClick={() => {
                setSelectedPolicyId(p.id);
                setAnchor(null);
              }}
            >
              <ListItemText
                primary={policyTagLabel(p)}
                secondary={
                  p.status && p.status !== PolicyStatus.ACTIVE ? (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {formatToTitleCase(String(p.status))}
                    </Typography>
                  ) : undefined
                }
              />
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </Box>
  );
};
